import Text from 'pf9-ui-components/built/elements/Text'
import ToggleSwitch from 'pf9-ui-components/built/elements/ToggleSwitch'
import { pick } from 'ramda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { listTablePrefs } from 'app/constants'
import RadioDropdownFilter from 'app/plugins/openstack/components/networks/dns/records/RadioDropdownFilter'
import InferActionParams from 'core/actions/InferActionParams'
import DocumentMeta from 'core/components/DocumentMeta'
import { DateAndTime } from 'core/components/listTable/cells/DateCell'
import PollingData from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import ListContainer from 'core/containers/ListContainer'
import Badge from 'core/elements/badge'
import { Item } from 'core/elements/dropdown/GlobalDropdownWithSearch'
import { GridViewColumn } from 'core/elements/grid/Grid'
import getGridDialogButton from 'core/elements/grid/helpers/getGridDialogButton'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import { HeaderRefreshPortal } from 'core/elements/header/portals'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { routeManager } from 'core/utils/routes'
import DeleteWorkerNodesDialog from 'k8s/components/capacity-and-health/DeleteWorkerNodesDialog'
import {
  GPUMemoryCellComponent,
  getK8sNodeStatus,
  WorkerNodeCellComponent,
} from 'k8s/components/capacity-and-health/ListWorkerNodes'
import { listKaapiMachineAllClusters } from 'k8s/components/kaapi/machines/actions'
import { kaapiMachinesSelectorAllClusters } from 'k8s/components/kaapi/machines/selectors'
import { isNilOrEmpty } from 'utils/fp'
import { listKaapiNodesForAllClusters } from '../kaapi/workload-clusters/nodes/actions'
import MachineStatusCell from '../kaapi/machines/MachineStatusCell'
import GPUDetailsModal from './GPUDetailsModal'

export enum ClusterNodesTabs {
  VIRTUALIZED_NODES = 'virtualized-nodes',
  PHYSICAL_NODES = 'physical-nodes',
}

type ActionParams = InferActionParams<typeof listKaapiMachineAllClusters>
type Params = ActionParams & {}

const defaultParams: Params & SortingState = {
  orderBy: 'nodeType',
  orderDirection: 'asc',
}

const usePrefParams = createUsePrefParamsHook('KaapiClusterNodes', listTablePrefs)

export const columns: GridViewColumn<any>[] = [
  {
    key: 'name',
    label: 'Name',
    CellComponent: WorkerNodeCellComponent,
  },
  {
    key: 'gpu.enabled',
    label: 'GPU Enabled',
    CellComponent: ({ value }) => <Text variant="body2">{value ? 'Yes' : 'No'}</Text>,
  },
  {
    key: 'gpu.model',
    label: 'GPU Model',
    CellComponent: ({ value }) => <Text variant="body2">{value || '-'}</Text>,
    display: false,
  },
  {
    key: 'gpu.partitioningStrategy',
    label: 'GPU Strategy',
    CellComponent: ({ value, item }) => <GPUDetailsModal item={item} value={value} />,
    display: false,
  },
  {
    key: 'gpu.details',
    label: 'GPU Details',
    CellComponent: ({ value }) => <Text variant="body2">{value || '-'}</Text>,
    display: false,
  },
  {
    key: 'gpu.count',
    label: 'GPU Count',
    CellComponent: ({ value }) => <Text variant="body2">{value || '-'}</Text>,
    display: false,
  },
  {
    key: 'gpu.replicas',
    label: 'GPU Replicas',
    CellComponent: ({ value }) => <Text variant="body2">{value || '-'}</Text>,
    display: false,
  },
  {
    key: 'gpu.memory',
    label: 'GPU Memory',
    CellComponent: GPUMemoryCellComponent,
    display: false,
  },
  {
    key: 'clusterName',
    label: 'Cluster Name',
    CellComponent: ({ value }) => {
      return (
        <SimpleLink
          src={routeManager.routes.kubernetes.infrastructure.cluster.path({ cluster: value })}
        >
          {value}
        </SimpleLink>
      )
    },
  },
  {
    key: 'nodeType',
    label: 'Node Type',
    disableSorting: true,
    CellComponent: (props) => {
      if (!props?.value) return <Text variant="body2">-</Text>
      return <Badge text={props.value} variant="primary" hideTooltip />
    },
  },
  // Un-comment this after having single backend API to get all clusters node status
  // {
  //   key: 'k8sNodeStatusType',
  //   label: 'K8s Node status',
  //   CellComponent: createGridStatusCell({
  //     dataFn: (node: NodeGroupSelector): StatusCellModel => {
  //       return getK8sNodeStatus(node)
  //     },
  //   }),
  // },
  {
    key: 'phase',
    label: 'Machine Status',
    CellComponent: MachineStatusCell,
    memoizeCell: false,
  },
  {
    key: 'machineDeployment.metadata.name',
    label: 'Node Group',
    display: false,
  },
  {
    key: 'machineDeployment.openStackMachineTemplate.spec.template.spec.flavor',
    label: 'Flavor',
  },
  {
    key: 'spec.version',
    label: 'K8s Version',
    formatFn: (value) => (isNilOrEmpty(value) ? '-' : value),
  },
  {
    key: 'metadata.creationTimestamp',
    label: 'Created',
    CellComponent: DateAndTime,
  },
  // {
  //   key: 'cpuUsage',
  //   label: 'CPU Usage (cores)',
  //   CellComponent: UsageProgress,
  // },
  // {
  //   key: 'memoryUsage',
  //   label: 'Memory Usage (GiB)',
  //   CellComponent: UsageProgress,
  // },
]

const ClusterVirtualizedNodes = () => {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedWorkerNode, setSelectedWorkerNode] = useState(null)
  const [filteredData, setFilteredData] = useState([])
  const [showGpuNodesOnly, setShowGpuNodesOnly] = useState(false)
  //persist the most recently selected cluster filter values across renders without triggering re-renders when it changes
  const lastSelectedClustersRef = useRef<Item[]>([])
  // Reload kaapi nodes for physical nodes on mount
  const { reload: reloadKaapiNodes } = useListAction(listKaapiNodesForAllClusters)

  const { loading: loadingKaapiMachines, reload: reloadKaapiMachines } = useListAction(
    listKaapiMachineAllClusters,
  )
  const machines = useSelector(kaapiMachinesSelectorAllClusters) || []

  // Filter machines into virtualized and physical nodes
  const getVirtualizedNodes = useCallback(
    () =>
      machines?.filter(
        (machine) => !machine?.isBYOMachine && (showGpuNodesOnly ? machine?.gpu?.enabled : true),
      ),
    [machines, showGpuNodesOnly],
  )

  const clusterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          machines
            ?.filter((machine) => !machine?.isBYOMachine)
            ?.map((machine) => machine?.clusterName),
        ),
      )?.map((name) => ({ label: name, value: name })),
    [machines],
  )

  const handleApplyClusterFilter = useCallback(
    (selectedItems: Item[]) => {
      lastSelectedClustersRef.current = selectedItems
      const virtualizedNodes = getVirtualizedNodes()
      if (!selectedItems?.length) {
        setFilteredData(virtualizedNodes)
        return
      }
      const selectedClusterNames = selectedItems?.map((item) => item?.value)
      setFilteredData(
        virtualizedNodes?.filter((vm) => selectedClusterNames?.includes(vm?.clusterName)),
      )
    },
    [getVirtualizedNodes],
  )

  const handleGpuToggle = useCallback(() => {
    setShowGpuNodesOnly((prev) => !prev)
  }, [])

  useEffect(() => {
    handleApplyClusterFilter(lastSelectedClustersRef.current)
  }, [showGpuNodesOnly, handleApplyClusterFilter])

  const isLastRunningNode = useMemo(
    () => machines?.length === 1 && machines[0]?.status?.phase === 'Running',
    [machines],
  )

  const batchActions = useMemo(
    () => [
      {
        label: 'Delete',
        icon: 'trash-alt',
        handleAction: (selectedItems) => {
          setSelectedWorkerNode(selectedItems)
          setShowDeleteDialog(true)
        },
      },
    ],
    [],
  )

  const rowMenuItems = useMemo(
    () => [
      {
        label: 'Delete',
        icon: 'trash-alt',
        handleClick: (workerNode) => {
          setSelectedWorkerNode([workerNode])
          setShowDeleteDialog(true)
        },
        refreshAfterSuccess: true,
        hideIfDisabled: true,
      },
    ],
    [],
  )

  useEffect(() => {
    reloadKaapiNodes(true, false)
  }, [])

  // This is done intentionally to avoid re-rendering of the component.
  const virtualizedNodesData = useMemo(() => getVirtualizedNodes(), [getVirtualizedNodes])

  return (
    <>
      <DocumentMeta title="Cluster Nodes" />
      <PollingData
        hidden
        loading={loadingKaapiMachines}
        onReload={reloadKaapiMachines}
        refreshDuration={1000 * 30}
      />
      <HeaderRefreshPortal>
        <RadioDropdownFilter
          width={300}
          label="Cluster"
          items={clusterOptions}
          loading={loadingKaapiMachines}
          data={virtualizedNodesData}
          handleApplyFilter={handleApplyClusterFilter}
          showLabel={false}
          align="left"
        />
      </HeaderRefreshPortal>
      {showDeleteDialog && (
        <DeleteWorkerNodesDialog
          rows={selectedWorkerNode}
          onClose={() => setShowDeleteDialog(false)}
          isLastRunningNode={isLastRunningNode}
          reloadKaapiMachines={reloadKaapiMachines}
        />
      )}
      <ListContainer<any, any>
        searchTargets={['name', 'gpuModel', 'clusterName', 'phase', 'k8sNodeStatusType']}
        uniqueIdentifier="uid"
        loading={loadingKaapiMachines}
        loadingMessage="Loading..."
        onRefresh={reloadKaapiMachines}
        hideRefreshButtonOnTop={true}
        multiSelection
        data={filteredData}
        columns={columns}
        getParamsUpdater={getParamsUpdater}
        label="Virtualized Nodes"
        showItemsCountInLabel
        batchActions={batchActions}
        rowMenuItems={rowMenuItems}
        showRowMenuForSingleRowActions
        {...pick(listTablePrefs, params)}
        extraToolbarContent={
          <ToggleSwitch
            active={showGpuNodesOnly}
            onClick={handleGpuToggle}
            label="Show GPU Nodes Only"
            data-testid="show-gpu-nodes-only-toggle-switch"
          />
        }
      />
    </>
  )
}

export default ClusterVirtualizedNodes
